import React, { useState, useEffect, useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { TrendingUp, ChevronDown } from 'lucide-react';
import { fetchPresupuestoDetalle } from '../../api';

const formatPrice = (value) => {
  const num = parseFloat(value) || 0;
  return num.toLocaleString('es-MX', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  // Formato YYYY-MM-DD o YYYY-MM
  const parts = dateStr.split('-');
  if (parts.length >= 2) {
    const year = parts[0];
    const month = parts[1];
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    const monthIndex = parseInt(month) - 1;
    return `${months[monthIndex]} ${year}`;
  }
  return dateStr;
};

export default function HorasXStatus({
  isDarkMode = true,
  selectedClientes = [],
  selectedMeses = [],
  selectedFabricantes = [],
  selectedServicios = [],
  selectedMonedas = [],
  selectedProveedores = [],
  filtrosDisponibles = {},
  tipoFacturacionFiltro = 'Todos'
}) {
  const [presupuesto, setPresupuesto] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clienteSeleccionado, setClienteSeleccionado] = useState('BBA');
  const [showClienteDropdown, setShowClienteDropdown] = useState(false);

  // Colores tema
  const cardClasses = isDarkMode
    ? 'bg-[#071a0f]/80 backdrop-blur-md border border-[#00FF66]/20 rounded-xl shadow-lg hover:border-[#00FF66]/40 transition-all'
    : 'bg-white border border-emerald-200 rounded-xl shadow-lg';
  const textPrimary = isDarkMode ? 'text-[#e8fff0]' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-[#a0c8b0]' : 'text-gray-600';
  const textMuted = isDarkMode ? 'text-[#7ea58f]' : 'text-gray-500';
  const bgHover = isDarkMode ? 'hover:bg-[#00FF66]/10' : 'hover:bg-emerald-50';
  const tableBorder = isDarkMode ? 'border-[#0e3d25]' : 'border-emerald-100';
  const bgRow = isDarkMode ? 'bg-[#0a2818]/40' : 'bg-white';

  // Cargar datos presupuestarios
  useEffect(() => {
    const loadPresupuesto = async () => {
      setLoading(true);
      try {
        const filters = {
          cliente: selectedClientes && selectedClientes.length > 0 ? selectedClientes[0] : undefined,
          mes_inicio: selectedMeses && selectedMeses.length > 0 ? selectedMeses[0] : undefined,
          mes_fin: selectedMeses && selectedMeses.length > 0 ? selectedMeses[selectedMeses.length - 1] : undefined,
          fabricante: selectedFabricantes && selectedFabricantes.length > 0 ? selectedFabricantes[0] : undefined,
          servicio: selectedServicios && selectedServicios.length > 0 ? selectedServicios[0] : undefined,
          moneda_fuente: selectedMonedas && selectedMonedas.length > 0 ? selectedMonedas[0] : 'USD',
          proveedor: selectedProveedores && selectedProveedores.length > 0 ? selectedProveedores[0] : undefined
        };

        console.log('🔄 Cargando presupuesto con filtros:', filters);
        const data = await fetchPresupuestoDetalle(filters);
        
        console.log('📊 Presupuesto recibido:', data);
        console.log('   Tipo:', typeof data);
        console.log('   Es Array:', Array.isArray(data));
        console.log('   Longitud:', Array.isArray(data) ? data.length : 'N/A');
        
        setPresupuesto(Array.isArray(data) ? data : []);
        
        // Si hay datos y se pasó un cliente específico, usarlo; si no hay cliente en filtros pero hay datos, usar el primero
        if (Array.isArray(data) && data.length > 0) {
          if (selectedClientes && selectedClientes.length > 0) {
            setClienteSeleccionado(selectedClientes[0]);
          } else {
            const primerCliente = data[0].Nombre_Cliente;
            if (primerCliente) setClienteSeleccionado(primerCliente);
          }
        }
      } catch (error) {
        console.error('❌ Error cargando presupuesto:', error);
        setPresupuesto([]);
      } finally {
        setLoading(false);
      }
    };

    loadPresupuesto();
  }, [selectedClientes, selectedMeses, selectedFabricantes, selectedServicios, selectedMonedas, selectedProveedores]);

  // Clientes únicos del backend
  const clientesUnicos = useMemo(() => {
    if (!Array.isArray(presupuesto)) return [];
    const clientes = new Set(presupuesto.map(r => r.Nombre_Cliente).filter(Boolean));
    return Array.from(clientes).sort();
  }, [presupuesto]);

  // Presupuesto filtrado por Tipo_Facturacion (prop del padre)
  const presupuestoFiltrado = useMemo(() => {
    if (!Array.isArray(presupuesto)) return [];
    if (!tipoFacturacionFiltro || tipoFacturacionFiltro === 'Todos') return presupuesto;
    return presupuesto.filter(r => r.Tipo_Facturacion === tipoFacturacionFiltro);
  }, [presupuesto, tipoFacturacionFiltro]);

  // Procesar datos para gráfica y tabla
  const { datosGrafica, datosTabla, headerInfo } = useMemo(() => {
    if (!Array.isArray(presupuestoFiltrado) || presupuestoFiltrado.length === 0) {
      return { datosGrafica: [], datosTabla: [], headerInfo: {} };
    }

    // Agrupar por mes
    const porMes = new Map();
    let oportunidad = '';
    let vigencia = '';
    let tipoFacturacion = '';

    presupuestoFiltrado.forEach(record => {
      const mes = record.Mes || record.Fecha_Consumo;
      if (!mes) return;

      if (!porMes.has(mes)) {
        porMes.set(mes, {
          mes: mes,
          costo_diseno: 0,
          costo_real: 0,
          precio_diseno: 0,
          precio_real: 0
        });
      }

      const datos = porMes.get(mes);
      datos.costo_diseno += parseFloat(record.Costo_Diseno) || 0;
      datos.costo_real += parseFloat(record.Costo_Real) || 0;
      datos.precio_diseno += parseFloat(record.Precio_Diseno) || 0;
      datos.precio_real += parseFloat(record.Precio_Real) || 0;

      if (!oportunidad) oportunidad = record.Oportunidad || '-';
      if (!vigencia) vigencia = record.Vigencia || '-';
      if (!tipoFacturacion) tipoFacturacion = record.Tipo_Facturacion || '-';
    });

    // Convertir a arrays y ordenar
    const grafica = Array.from(porMes.values())
      .sort((a, b) => a.mes.localeCompare(b.mes))
      .map(item => ({
        ...item,
        mes_label: formatDate(item.mes)
      }));

    // Datos tabla con diferencias
    const tabla = grafica.map(item => ({
      ...item,
      diferencia: item.precio_real - item.costo_real,
      diferencia_diseno: item.precio_diseno - item.costo_diseno
    }));

    return {
      datosGrafica: grafica,
      datosTabla: tabla,
      headerInfo: {
        oportunidad,
        vigencia,
        tipoFacturacion
      }
    };
  }, [presupuestoFiltrado]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className={`${
          isDarkMode
            ? 'bg-[#03120a]/95 border-2 border-[#00FF66]/40'
            : 'bg-white/95 border-2 border-emerald-300'
        } backdrop-blur-md rounded-lg p-3 shadow-xl`}>
          <div className={`${textPrimary} font-semibold text-sm mb-2`}>{label}</div>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center gap-2 mb-1">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: entry.color }} />
              <span className={`${textSecondary} text-xs`}>
                {entry.name}: {formatPrice(entry.value)}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`${cardClasses} p-6 mt-6`}>
      {/* Header */}
      <div className="mb-6">
        <h3 className={`${isDarkMode ? 'text-[#00FF66]' : textPrimary} text-xl font-bold mb-3 flex items-center gap-2`}>
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-md border"
            style={{
              backgroundColor: isDarkMode ? 'rgba(0,255,102,0.14)' : 'rgba(0,255,102,0.1)',
              borderColor: isDarkMode ? 'rgba(0,255,102,0.35)' : 'rgba(0,255,102,0.3)'
            }}>
            <TrendingUp width={15} height={15} color="#00FF66" />
          </span>
          Análisis de Presupuesto
        </h3>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {/* Cliente */}
          <div className={`p-3 rounded-lg border ${isDarkMode ? 'border-[#00FF66]/20 bg-[#03120a]/50' : 'border-emerald-200 bg-emerald-50'}`}>
            <div className={`${textMuted} text-xs mb-1`}>Cliente</div>
            <div className="relative">
              <button
                onClick={() => setShowClienteDropdown(!showClienteDropdown)}
                className={`w-full text-left ${textPrimary} font-semibold text-sm flex items-center justify-between`}
              >
                <span>{clienteSeleccionado}</span>
                <ChevronDown size={16} className={showClienteDropdown ? 'rotate-180' : ''} />
              </button>
              {showClienteDropdown && clientesUnicos.length > 0 && (
                <div className={`absolute top-full left-0 right-0 mt-1 rounded-lg border z-20 ${
                  isDarkMode ? 'bg-[#071a0f] border-[#00FF66]/30' : 'bg-white border-emerald-200'
                }`}>
                  {clientesUnicos.map(cliente => (
                    <button
                      key={cliente}
                      onClick={() => {
                        setClienteSeleccionado(cliente);
                        setShowClienteDropdown(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm ${bgHover} ${cliente === clienteSeleccionado ? (isDarkMode ? 'text-[#00FF66]' : 'text-emerald-600') : textPrimary}`}
                    >
                      {cliente}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Oportunidad */}
          <div className={`p-3 rounded-lg border ${isDarkMode ? 'border-[#00FF66]/20 bg-[#03120a]/50' : 'border-emerald-200 bg-emerald-50'}`}>
            <div className={`${textMuted} text-xs mb-1`}>Oportunidad</div>
            <div className={`${textPrimary} font-semibold text-sm truncate`}>{headerInfo.oportunidad || '-'}</div>
          </div>

          {/* Vigencia */}
          <div className={`p-3 rounded-lg border ${isDarkMode ? 'border-[#00FF66]/20 bg-[#03120a]/50' : 'border-emerald-200 bg-emerald-50'}`}>
            <div className={`${textMuted} text-xs mb-1`}>Vigencia</div>
            <div className={`${textPrimary} font-semibold text-sm truncate`}>{headerInfo.vigencia || '-'}</div>
          </div>

          {/* Tipo Facturación */}
          <div className={`p-3 rounded-lg border ${isDarkMode ? 'border-[#00FF66]/20 bg-[#03120a]/50' : 'border-emerald-200 bg-emerald-50'}`}>
            <div className={`${textMuted} text-xs mb-1`}>Tipo Facturación</div>
            <div className={`${textPrimary} font-semibold text-sm truncate`}>{headerInfo.tipoFacturacion || '-'}</div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-96">
          <div className={textMuted}>Cargando datos...</div>
        </div>
      ) : !Array.isArray(presupuesto) || presupuesto.length === 0 ? (
        <div className="flex items-center justify-center h-96">
          <div className={textMuted}>No hay datos disponibles</div>
        </div>
      ) : datosGrafica.length === 0 ? (
        <div className="flex items-center justify-center h-96">
          <div className={textMuted}>
            {tipoFacturacionFiltro !== 'Todos'
              ? `Sin datos para tipo "${tipoFacturacionFiltro}"`
              : 'No hay datos disponibles'}
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Gráfica */}
          <div>
            <h4 className={`${textSecondary} text-sm font-semibold mb-4`}>Evolución Mensual: Costo Diseño vs Costo Real</h4>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={datosGrafica} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorDiseno" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorReal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#EF4444" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#0e3d25' : '#E5E7EB'} />
                <XAxis
                  dataKey="mes_label"
                  stroke={isDarkMode ? '#88b79d' : '#6B7280'}
                  style={{ fontSize: '12px' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  stroke={isDarkMode ? '#88b79d' : '#6B7280'}
                  style={{ fontSize: '12px' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  wrapperStyle={{
                    fontSize: '12px',
                    color: isDarkMode ? '#d9d9d9' : '#000'
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="costo_diseno"
                  stroke="#8B5CF6"
                  strokeWidth={2}
                  dot={{ fill: '#8B5CF6', r: 4 }}
                  name="Costo Diseño"
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="costo_real"
                  stroke="#EF4444"
                  strokeWidth={2}
                  dot={{ fill: '#EF4444', r: 4 }}
                  name="Costo Real"
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Tabla Mensual */}
          <div>
            <h4 className={`${textSecondary} text-sm font-semibold mb-4`}>Comparativa Mensual</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={`border-b ${tableBorder}`}>
                    <th className={`${textSecondary} font-semibold px-4 py-3 text-left`}>Mes</th>
                    <th className={`${textSecondary} font-semibold px-4 py-3 text-right`}>Costo Diseño</th>
                    <th className={`${textSecondary} font-semibold px-4 py-3 text-right`}>Costo Real</th>
                    <th className={`${textSecondary} font-semibold px-4 py-3 text-right`}>Diferencia</th>
                    <th className={`${textSecondary} font-semibold px-4 py-3 text-right`}>Precio Diseño</th>
                    <th className={`${textSecondary} font-semibold px-4 py-3 text-right`}>Precio Real</th>
                    <th className={`${textSecondary} font-semibold px-4 py-3 text-right`}>Margen Real</th>
                  </tr>
                </thead>
                <tbody>
                  {datosTabla.map((row, idx) => {
                    const margenReal = row.precio_real - row.costo_real;
                    const diferenciaPositiva = margenReal >= 0;

                    return (
                      <tr
                        key={idx}
                        className={`border-b ${tableBorder} ${bgRow} hover:${isDarkMode ? 'bg-[#0e3d25]/30' : 'bg-emerald-50'}`}
                      >
                        <td className={`${textPrimary} px-4 py-3 font-semibold`}>{row.mes_label}</td>
                        <td className={`${textPrimary} px-4 py-3 text-right`}>{formatPrice(row.costo_diseno)}</td>
                        <td className={`${textPrimary} px-4 py-3 text-right`}>{formatPrice(row.costo_real)}</td>
                        <td className={`px-4 py-3 text-right font-semibold ${
                          row.costo_real < row.costo_diseno ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {formatPrice(row.costo_diseno - row.costo_real)}
                        </td>
                        <td className={`${textPrimary} px-4 py-3 text-right`}>{formatPrice(row.precio_diseno)}</td>
                        <td className={`${textPrimary} px-4 py-3 text-right`}>{formatPrice(row.precio_real)}</td>
                        <td className={`px-4 py-3 text-right font-semibold ${
                          diferenciaPositiva ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {formatPrice(margenReal)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}